/**
 * Leviathan - Leads List Page
 * Quasar Prime LLC
 *
 * List and manage sales leads with filtering and bulk actions
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  ChevronLeft,
  ChevronRight,
  Users,
  Phone,
  Mail,
  Calendar,
  Target,
  TrendingUp,
  UserPlus,
  Trash2,
  Edit,
  Eye,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

// Lead status configuration
const LEAD_STATUSES = [
  { value: 'NEW', label: 'New', color: 'bg-blue-500' },
  { value: 'CONTACTED', label: 'Contacted', color: 'bg-cyan-500' },
  { value: 'QUALIFIED', label: 'Qualified', color: 'bg-indigo-500' },
  { value: 'PROPOSAL', label: 'Proposal', color: 'bg-purple-500' },
  { value: 'NEGOTIATION', label: 'Negotiation', color: 'bg-amber-500' },
  { value: 'WON', label: 'Won', color: 'bg-emerald-500' },
  { value: 'LOST', label: 'Lost', color: 'bg-red-500' },
  { value: 'DORMANT', label: 'Dormant', color: 'bg-slate-400' },
];

const LEAD_SOURCES = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'COLD_CALL', label: 'Cold Call' },
  { value: 'TRADE_SHOW', label: 'Trade Show' },
  { value: 'ADVERTISEMENT', label: 'Advertisement' },
  { value: 'SOCIAL_MEDIA', label: 'Social Media' },
  { value: 'EXISTING_CUSTOMER', label: 'Existing Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'OTHER', label: 'Other' },
];

interface Lead {
  id: string;
  leadNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  source: string;
  score: number;
  estimatedValue: number | null;
  estimatedVolume: number | null;
  nextFollowUpDate: string | null;
  lastContactDate: string | null;
  createdAt: string;
  assignedTo?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  _count?: {
    activities: number;
  };
}

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export default function LeadsList() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters from URL params
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(
    searchParams.get('status')?.split(',').filter(Boolean) || []
  );
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || '');
  const [assignedFilter, setAssignedFilter] = useState(searchParams.get('assignedToId') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);

  const pageSize = 25;

  // Fetch leads
  const { data: leadsData, isLoading } = useQuery({
    queryKey: ['leads', search, statusFilter, sourceFilter, assignedFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter.length > 0) params.append('status', statusFilter.join(','));
      if (sourceFilter) params.append('source', sourceFilter);
      if (assignedFilter) params.append('assignedToId', assignedFilter);
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      const res = await fetch(`${API_URL}/api/leads?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch leads');
      return res.json();
    },
  });

  // Fetch users for assignment filter
  const { data: usersData } = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Delete lead mutation
  const deleteMutation = useMutation({
    mutationFn: async (leadId: string) => {
      const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to delete lead');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  // Bulk assign mutation
  const bulkAssignMutation = useMutation({
    mutationFn: async ({ leadIds, userId }: { leadIds: string[]; userId: string }) => {
      const res = await fetch(`${API_URL}/api/leads/bulk-assign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ leadIds, userId }),
      });
      if (!res.ok) throw new Error('Failed to assign leads');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setSelectedLeads([]);
      setShowBulkAssign(false);
    },
  });

  const leads: Lead[] = leadsData?.data || [];
  const totalPages = leadsData?.meta?.totalPages || 1;
  const total = leadsData?.meta?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (search) params.set('search', search);
    else params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const toggleStatusFilter = (status: string) => {
    const newStatuses = statusFilter.includes(status)
      ? statusFilter.filter((s) => s !== status)
      : [...statusFilter, status];
    setStatusFilter(newStatuses);
    setPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeads(leads.map((l) => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId: string, checked: boolean) => {
    if (checked) {
      setSelectedLeads([...selectedLeads, leadId]);
    } else {
      setSelectedLeads(selectedLeads.filter((id) => id !== leadId));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = LEAD_STATUSES.find((s) => s.value === status);
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium text-white ${statusConfig?.color || 'bg-slate-500'}`}
      >
        {statusConfig?.label || status}
      </span>
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-400';
    if (score >= 40) return 'text-amber-400';
    return 'text-slate-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Leads</h1>
          <p className="text-slate-400">
            {total} total lead{total !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/sales/leads/new"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Lead
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company, contact, email, or lead number..."
              className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
              showFilters
                ? 'bg-amber-500/10 border-amber-500 text-amber-500'
                : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            Search
          </button>
        </form>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                {LEAD_STATUSES.map((status) => (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => toggleStatusFilter(status.value)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      statusFilter.includes(status.value)
                        ? `${status.color} text-white`
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Source Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Source
                </label>
                <select
                  value={sourceFilter}
                  onChange={(e) => {
                    setSourceFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  <option value="">All Sources</option>
                  {LEAD_SOURCES.map((source) => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Assigned To Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Assigned To
                </label>
                <select
                  value={assignedFilter}
                  onChange={(e) => {
                    setAssignedFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  <option value="">All Users</option>
                  {usersData?.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(statusFilter.length > 0 || sourceFilter || assignedFilter) && (
              <button
                type="button"
                onClick={() => {
                  setStatusFilter([]);
                  setSourceFilter('');
                  setAssignedFilter('');
                  setPage(1);
                }}
                className="text-amber-500 hover:text-amber-400 text-sm"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/50 rounded-xl p-4 flex items-center justify-between">
          <p className="text-amber-500">
            {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBulkAssign(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-sm font-medium transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Assign
            </button>
            <button
              onClick={() => setSelectedLeads([])}
              className="text-slate-400 hover:text-slate-300 text-sm"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssign && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">
              Assign {selectedLeads.length} Leads
            </h3>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  bulkAssignMutation.mutate({
                    leadIds: selectedLeads,
                    userId: e.target.value,
                  });
                }
              }}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="">Select user...</option>
              {usersData?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowBulkAssign(false)}
                className="px-4 py-2 text-slate-400 hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Loading leads...</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-600" />
            <p className="text-slate-400">No leads found</p>
            <Link
              to="/sales/leads/new"
              className="inline-block mt-4 text-amber-500 hover:text-amber-400"
            >
              Create your first lead
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900/50 border-b border-slate-700">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Lead
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Est. Value
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Follow-up
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-slate-900/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                        className="rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/sales/leads/${lead.id}`}
                        className="hover:text-amber-400 transition-colors"
                      >
                        <p className="font-medium text-slate-200">{lead.companyName}</p>
                        <p className="text-xs text-slate-500">{lead.leadNumber}</p>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-300">{fullName({ firstName: lead.contactFirstName, lastName: lead.contactLastName }) ?? '-'}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {lead.email && (
                          <a
                            href={`mailto:${lead.email}`}
                            className="text-slate-500 hover:text-amber-400"
                            title={lead.email}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {lead.phone && (
                          <a
                            href={`tel:${lead.phone}`}
                            className="text-slate-500 hover:text-amber-400"
                            title={lead.phone}
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(lead.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Target className={`w-4 h-4 ${getScoreColor(lead.score)}`} />
                        <span className={`font-medium ${getScoreColor(lead.score)}`}>
                          {lead.score}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {lead.estimatedValue ? (
                        <div className="flex items-center gap-1 text-emerald-400">
                          <TrendingUp className="w-4 h-4" />
                          <span>${lead.estimatedValue.toLocaleString()}</span>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {lead.assignedTo ? (
                        <span className="text-slate-300">
                          {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                        </span>
                      ) : (
                        <span className="text-slate-500">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {lead.nextFollowUpDate ? (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-slate-300 text-sm">
                            {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/sales/leads/${lead.id}`}
                          className="p-1.5 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/sales/leads/${lead.id}/edit`}
                          className="p-1.5 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this lead?')) {
                              deleteMutation.mutate(lead.id);
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              Showing {(page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, total)} of {total} leads
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-slate-300">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="p-2 text-slate-400 hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
